import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Play,
  CalendarDays,
  Wallet,
  Clock,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Sparkles,
  Scissors,
} from 'lucide-react';
import { useAppointments, useOtherIncomes, useStats } from '@/lib/queries';
import { useAuthStore } from '@/store/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar } from '@/components/ui/avatar';
import { AppointmentCard } from '@/components/AppointmentCard';
import { ScopeSwitcher } from '@/components/ScopeSwitcher';
import { addMinutesToTime, fmtDateLong, fmtMoney, HHMMtoMin, todayISO } from '@/lib/format';
import { cn } from '@/lib/cn';

const useNow = () => {
  const [t, setT] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setT(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  return t;
};

const greet = (h: number) => {
  if (h < 5) return 'Доброй ночи';
  if (h < 12) return 'Доброе утро';
  if (h < 18) return 'Добрый день';
  return 'Добрый вечер';
};

export const DashboardPage = () => {
  const today = todayISO();
  const { master } = useAuthStore();
  const { data: appts = [] } = useAppointments({ from: today, to: today });
  const { data: otherIncomes = [] } = useOtherIncomes({ from: today, to: today });
  const { data: stats } = useStats({ from: today, to: today });
  const now = useNow();

  const active = useMemo(() => {
    const minutesNow = now.getHours() * 60 + now.getMinutes();
    return appts.find((a: any) => {
      if (a.status === 'in_progress') return true;
      if (['cancelled', 'rescheduled', 'no_show', 'completed'].includes(a.status)) return false;
      const s = HHMMtoMin(a.start_time);
      const e = s + a.planned_duration_min;
      return minutesNow >= s && minutesNow < e;
    });
  }, [appts, now]);

  const summary = useMemo(() => {
    // Match the API: any appointment that wasn't cancelled, rescheduled or
    // marked no-show counts toward revenue, so a just-created paid record is
    // visible without having to first "start" and "finish" it.
    const counted = appts.filter(
      (a: any) => !['cancelled', 'rescheduled', 'no_show'].includes(a.status),
    );
    const revenueAppts = counted.reduce((s: number, a: any) => {
      const extras = (a.extras ?? []).reduce((ss: number, e: any) => ss + Number(e.price ?? 0), 0);
      return s + Number(a.work_price ?? 0) + Number(a.hair_price ?? 0) + Number(a.tangle_price ?? 0) + extras;
    }, 0);
    const minutes = appts.reduce(
      (s: number, a: any) =>
        ['cancelled', 'rescheduled', 'no_show'].includes(a.status) ? s : s + a.planned_duration_min,
      0,
    );
    const upcomingCount = appts.filter(
      (a: any) => !['cancelled', 'rescheduled', 'no_show', 'completed'].includes(a.status),
    ).length;
    const otherRevenue = otherIncomes.reduce((s: number, o: any) => s + Number(o.amount ?? 0), 0);
    const revenue = revenueAppts + otherRevenue;
    const hairExpenses = stats?.finance?.expenses_hair ?? 0;
    const otherExpenses = stats?.finance?.expenses_other ?? 0;
    const profit = stats?.finance?.profit ?? revenue - hairExpenses - otherExpenses;
    return {
      count: appts.length,
      upcoming: upcomingCount,
      revenue,
      hairExpenses,
      profit,
      hours: Math.round(minutes / 6) / 10,
    };
  }, [appts, otherIncomes, stats]);

  return (
    <div className="animate-in-up space-y-5 pb-2">
      {/* Hero header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Avatar src={master?.photo_url} name={master?.name} size="lg" />
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {fmtDateLong(now)}
            </p>
            <h1 className="text-xl font-bold tracking-tight">
              {greet(now.getHours())}, {master?.name?.split(' ')[0]}
            </h1>
          </div>
        </div>
        <ScopeSwitcher />
      </div>

      {/* Active appointment — gradient card */}
      {active && (
        <Link to={`/appointments/${active.id}`} className="block">
          <div className="relative overflow-hidden rounded-3xl bg-brand p-5 text-white shadow-soft-lg">
            <div
              aria-hidden
              className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl"
            />
            <div
              aria-hidden
              className="absolute -bottom-12 -left-8 h-40 w-40 rounded-full bg-white/10 blur-2xl"
            />

            <div className="relative space-y-3">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-90">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                </span>
                Сейчас идёт
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold">{active.client?.name}</div>
                  <div className="mt-1 flex items-center gap-3 text-sm text-white/85">
                    <span className="flex items-center gap-1.5 tabular">
                      <Clock className="h-3.5 w-3.5" />
                      {active.start_time}–
                      {addMinutesToTime(active.start_time, active.planned_duration_min)}
                    </span>
                    <span>· {active.service?.name}</span>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5" />
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-2.5">
        <KpiTile icon={CalendarDays} label="Записей" value={String(summary.count)} tint="primary" />
        <KpiTile icon={Clock} label="Часов" value={summary.hours.toFixed(1)} tint="accent" />
        <KpiTile
          icon={Wallet}
          label="Выручка"
          value={fmtMoney(summary.revenue)}
          tint="success"
          compact
        />
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <KpiTile
          icon={Scissors}
          label="Расходы (волосы)"
          value={fmtMoney(summary.hairExpenses)}
          tint="muted"
          compact
        />
        <KpiTile
          icon={summary.profit >= 0 ? TrendingUp : TrendingDown}
          label="Прибыль"
          value={fmtMoney(summary.profit)}
          tint={summary.profit >= 0 ? 'success' : 'destructive'}
          compact
        />
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2.5">
        <QuickAction
          to="/appointments/new"
          icon={Plus}
          title="Новая запись"
          subtitle="за 30 секунд"
          accent
        />
        <QuickAction
          to="/stats"
          icon={TrendingUp}
          title="Статистика"
          subtitle="отчёты и графики"
        />
      </div>

      {/* Today's appointments */}
      <div className="space-y-2.5">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-base font-bold tracking-tight">Записи на сегодня</h2>
            <p className="text-xs text-muted-foreground">
              {summary.upcoming > 0
                ? `${summary.upcoming} предстоит`
                : summary.count
                ? 'все записи завершены'
                : ''}
            </p>
          </div>
          {!!summary.count && (
            <Link to="/calendar" className="text-xs font-semibold text-primary">
              Календарь →
            </Link>
          )}
        </div>

        {!appts.length ? (
          <EmptyDay />
        ) : (
          <div className="space-y-2">
            {appts.map((a: any) => (
              <AppointmentCard key={a.id} appointment={a} />
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

const KpiTile = ({
  icon: Icon,
  label,
  value,
  tint,
  compact,
}: {
  icon: any;
  label: string;
  value: string;
  tint: 'primary' | 'accent' | 'success' | 'muted' | 'destructive';
  compact?: boolean;
}) => {
  const tints = {
    primary: 'text-primary bg-primary-soft',
    accent: 'text-accent bg-accent-soft',
    success: 'text-success bg-success-soft',
    muted: 'text-muted-foreground bg-secondary',
    destructive: 'text-destructive bg-destructive/10',
  };
  return (
    <Card className="overflow-hidden">
      <CardContent className="space-y-2 p-3">
        <span
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-lg',
            tints[tint],
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <div
            className={cn(
              'font-bold tracking-tight tabular',
              compact ? 'text-base' : 'text-2xl',
            )}
          >
            {value}
          </div>
          <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const QuickAction = ({
  to,
  icon: Icon,
  title,
  subtitle,
  accent,
}: {
  to: string;
  icon: any;
  title: string;
  subtitle: string;
  accent?: boolean;
}) => (
  <Link to={to}>
    <div
      className={cn(
        'group flex h-full items-center justify-between gap-2 rounded-2xl border border-border/60 bg-card p-3.5 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-soft-md',
        accent && 'border-transparent bg-brand text-white',
      )}
    >
      <div className="min-w-0">
        <div
          className={cn(
            'truncate text-sm font-bold',
            accent ? 'text-white' : 'text-foreground',
          )}
        >
          {title}
        </div>
        <div
          className={cn(
            'truncate text-xs',
            accent ? 'text-white/80' : 'text-muted-foreground',
          )}
        >
          {subtitle}
        </div>
      </div>
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-transform group-hover:scale-110',
          accent ? 'bg-white/20 text-white' : 'bg-primary-soft text-primary',
        )}
      >
        <Icon className="h-4.5 w-4.5" strokeWidth={2.2} />
      </span>
    </div>
  </Link>
);

const EmptyDay = () => (
  <Card>
    <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary">
        <Sparkles className="h-6 w-6" />
      </div>
      <div>
        <div className="font-semibold">Свободный день</div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Самое время отдохнуть или добавить новую запись
        </p>
      </div>
      <Link to="/appointments/new">
        <Button size="sm" variant="soft">
          <Plus className="h-4 w-4" /> Создать запись
        </Button>
      </Link>
    </CardContent>
  </Card>
);
